import {
  ComponentType,
  ReactNode,
  createContext,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';

import { useBreakpoint } from '@electricjs/arc';
import { useIsEmbedded } from '@/hooks/useIsEmbedded';

type PageTitleState = {
  title?: string;
  leftSideAfterTitleContent?: ComponentType;
};

type PageTitleContextType = PageTitleState & {
  setPageTitleState: (state: PageTitleState) => void;
};

const PageTitleContext = createContext<PageTitleContextType>({
  setPageTitleState: () => {},
});

export const PageTitleProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<PageTitleState>({});

  return (
    <PageTitleContext.Provider
      value={{
        title: state.title,
        leftSideAfterTitleContent: state.leftSideAfterTitleContent,
        setPageTitleState: setState,
      }}>
      {children}
    </PageTitleContext.Provider>
  );
};

export const usePageTitleContext = () => useContext(PageTitleContext);

/**
 * Registers the page title in the TopNav on tablet+ and returns props to
 * spread onto <Header> so the title falls back to the page body on mobile.
 *
 * Usage:
 *   const titleProps = usePageTitle('My Page');
 *   <Header {...titleProps} subtitle="..." />
 */
export const usePageTitle = (
  title?: string,
  leftSideAfterTitleContent?: ComponentType
): Pick<PageTitleState, 'title' | 'leftSideAfterTitleContent'> & {
  mb?: number | string;
} => {
  const { setPageTitleState } = usePageTitleContext();
  const { md } = useBreakpoint();
  const { isEmbedded } = useIsEmbedded();

  useLayoutEffect(() => {
    setPageTitleState({ title, leftSideAfterTitleContent });
    return () => setPageTitleState({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, leftSideAfterTitleContent]);

  if (md && !isEmbedded) return { mb: 0 };
  return {
    title,
    ...(leftSideAfterTitleContent ? { leftSideAfterTitleContent } : {}),
  };
};
